import { useCallback, useEffect, useState } from "react";
import { Download, Eye, FileText, Loader2, Paperclip, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { formatDate, formatIsoDay, formatMoney, formatTRY } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { uploadDocs } from "@/components/insaat/ExpenseDialog";
import { ACCEPT, TypeBadge, checkFiles, fileSize } from "@/components/insaat/common";
import { ExpenseHistory } from "@/components/insaat/ExpenseHistory";

export async function openDoc(d, download = false) {
  const w = download ? null : window.open("", "_blank");
  try {
    const { data } = await api.get(`/insaat/documents/${d.id}/url`, { params: { download } });
    if (w) w.location = data.url; else window.location.href = data.url;
  } catch (e) { w?.close(); toast.error(apiError(e)); }
}

function DocList({ expense, onChanged }) {
  const { can } = useAuth();
  const [docs, setDocs] = useState(null);
  const [progress, setProgress] = useState(null);
  const load = useCallback(() => api.get(`/insaat/expenses/${expense.id}/documents`).then(({ data }) => setDocs(data.items)).catch((e) => { setDocs([]); toast.error(apiError(e)); }), [expense.id]);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    const files = [...e.target.files]; e.target.value = "";
    const err = checkFiles(files);
    if (err) return toast.error(err);
    setProgress(0);
    try { await uploadDocs(expense.id, files, setProgress); toast.success(`${files.length} doküman yüklendi`); load(); onChanged?.(); } catch (er) { toast.error(apiError(er)); }
    setProgress(null);
  };
  const remove = async (d) => {
    if (!window.confirm(`${d.file_name} gider kaydından kaldırılsın mı? (Dosya denetim için depoda saklanır)`)) return;
    try { await api.delete(`/insaat/documents/${d.id}`); load(); onChanged?.(); } catch (er) { toast.error(apiError(er)); }
  };
  const canUpload = can("insaat_gider", "create") || can("insaat_gider", "edit");
  return (
    <div className="space-y-2" data-testid="expense-doc-list">
      {!docs ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" /> : docs.length === 0 ? <div className="text-[12px] text-slate-400">Doküman yok</div> : docs.map((d) => (
        <div key={d.id} className="flex items-center gap-2 border border-slate-200 rounded px-2 py-1.5 text-[12px]" data-testid={`doc-row-${d.id}`}>
          <FileText className="h-4 w-4 text-slate-500 shrink-0" />
          <div className="flex-1 min-w-0"><div className="truncate font-medium">{d.file_name}</div><div className="text-[11px] text-slate-400">{fileSize(d.size)} · {d.uploaded_by_name} · {formatDate(d.uploaded_at)}</div></div>
          <Button size="icon" variant="ghost" className="h-7 w-7" title="Görüntüle" onClick={() => openDoc(d)} data-testid={`doc-view-${d.id}`}><Eye className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" title="İndir" onClick={() => openDoc(d, true)} data-testid={`doc-download-${d.id}`}><Download className="h-3.5 w-3.5" /></Button>
          {can("insaat_gider", "edit") && <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" title="Kaldır" onClick={() => remove(d)} data-testid={`doc-remove-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
        </div>))}
      {canUpload && (progress != null ? <div data-testid="doc-upload-progress"><Progress value={progress} className="h-2" /><div className="text-[11px] text-slate-500 mt-1">Yükleniyor… %{progress}</div></div> : (
        <label className="inline-flex items-center gap-2 h-8 px-3 rounded-md border border-dashed border-slate-300 text-[12px] cursor-pointer hover:bg-slate-50" data-testid="doc-add-button">
          <Paperclip className="h-3.5 w-3.5" />Doküman ekle<input type="file" multiple accept={ACCEPT} className="hidden" onChange={add} data-testid="doc-add-input" /></label>))}
    </div>
  );
}

const Row = ({ k, v, testid }) => <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 text-[13px]"><span className="text-slate-500">{k}</span><span className="text-right" data-testid={testid}>{v}</span></div>;

export default function ExpenseViewDialog({ expense: e, docsOnly, onClose, onChanged }) {
  const { can } = useAuth();
  const pay = e.payment;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="expense-view-dialog">
        <DialogHeader><DialogTitle>{docsOnly ? `Dokümanlar · ${e.no}` : `${e.no} · ${e.name}`}</DialogTitle><DialogDescription className="sr-only">İnşaat gideri ayrıntıları</DialogDescription></DialogHeader>
        {!docsOnly && <div>
          <Row k="Tarih" v={formatIsoDay(e.date)} /><Row k="Gider Kalemi" v={e.category_name} testid="expense-view-category" />
          <Row k="Gider Türü" v={<TypeBadge type={e.gider_turu} label={e.gider_turu_label} />} testid="expense-view-type" />
          {e.quantity != null && <Row k="Miktar × Birim Fiyat" v={<span className="font-mono">{e.quantity_str} {e.unit} × {formatMoney(e.unit_price, e.currency)}</span>} testid="expense-view-qty" />}
          {e.worker_name && <Row k="Usta / Taşeron" v={`${e.worker_name} · ${e.labor_mode_label}`} testid="expense-view-worker" />}
          {e.provider_name && <Row k="Hizmet Veren" v={e.provider_name} testid="expense-view-provider" />}
          {e.service_description && <Row k="Hizmet Tanımı" v={e.service_description} />}
          <Row k="Tutar" v={<span className="font-mono">{formatMoney(e.amount, e.currency)}</span>} />
          {e.currency !== "TRY" && <Row k="Kur" v={<span className="font-mono">{e.fx_rate} ({e.fx_date})</span>} />}
          <Row k="TL karşılığı" v={<b className="font-mono">{formatTRY(e.amount_try)}</b>} testid="expense-view-try" />
          <Row k="Ödeme / Kayıt" v={pay.type === "account" ? `${pay.account_name} (kasa/banka çıkışı)` : pay.type === "supplier" ? `${pay.supplier_code} ${pay.supplier_name} (tedarikçi borcu)` : "Finans kaydı yok"} testid="expense-view-payment" />
          <Row k="Açıklama" v={e.description || "—"} />
          <Row k="Kaydeden" v={`${e.created_by_name} · ${formatDate(e.created_at)}`} />
          {e.updated_by_name && <Row k="Son düzenleyen" v={`${e.updated_by_name} · ${formatDate(e.updated_at)}`} />}
        </div>}
        <div className="pt-2"><div className="text-[12px] font-semibold text-slate-600 mb-2">Dokümanlar</div>
          {can("insaat_doc") ? <DocList expense={e} onChanged={onChanged} /> : <div className="text-[12px] text-slate-400" data-testid="doc-no-permission">Doküman görüntüleme yetkiniz yok</div>}</div>
        {!docsOnly && <div className="pt-1"><ExpenseHistory id={e.id} /></div>}
      </DialogContent>
    </Dialog>
  );
}
